<template>
  <Listbox
    as="div"
    v-model="content"
    :disabled="
      disabled ||
      formDisabled ||
      (!Array.isArray(content) && resolvedOptions.length <= 1) ||
      (Array.isArray(content) && resolvedOptions.length === 0)
    "
    :multiple="Array.isArray(content)">
    <ListboxLabel v-if="label" class="block mb-2 text-sm font-medium leading-6 text-neutral-900 dark:text-white">
      {{ label }}
    </ListboxLabel>

    <div class="flex">
      <Float
        placement="bottom"
        :offset="4"
        :auto-placement="{ allowedPlacements: ['top', 'bottom'], crossAxis: true }"
        :z-index="70"
        strategy="fixed"
        adaptive-width
        leave="transition ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
        tailwindcss-origin-class
        portal>
        <ListboxButton
          class="relative pr-10 w-full min-w-0 cursor-pointer shadow-xs py-1.5 pl-3 text-left ring-1 ring-inset focus:outline-hidden focus:ring-2 sm:text-sm sm:leading-6"
          :class="[classes, selectClass, suffixIcon ? 'rounded-l-md' : 'rounded-md']">
          <span class="block truncate">
            {{ selectedName }}
          </span>
          <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <i
              v-if="
                modelValue &&
                typeof resolvedOptions === 'object' &&
                !Array.isArray(resolvedOptions) &&
                placeholder &&
                !disabled &&
                !formDisabled
              "
              @click.stop="content = Array.isArray(content) ? [] : null"
              class="fa-sharp fa-regular fa-times text-neutral-400 hover:text-neutral-500 dark:hover:text-neutral-300 cursor-pointer mr-1 pointer-events-auto text-sm"
              aria-hidden="true"></i>
            <i class="fa-sharp fa-regular fa-chevron-down text-neutral-400" aria-hidden="true"></i>
          </span>
        </ListboxButton>

        <ListboxOptions
          :class="[
            up ? 'bottom-full mb-1' : mobileUp ? 'bottom-full mb-1 sm:bottom-auto sm:mb-0' : null,
            'max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-neutral-700 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm'
          ]">
          <template v-if="typeof resolvedOptions === 'object' && !Array.isArray(resolvedOptions)">
            <div v-for="(options, groupName) in resolvedOptions" :key="groupName">
              <h4
                class="sticky top-0 z-10 p-3 pb-2 text-xs font-bold text-black uppercase bg-white dark:text-white dark:bg-neutral-700">
                {{ groupName }}
              </h4>
              <ListboxOption
                as="template"
                v-for="(option, o) in options"
                :key="idKey ? option[idKey] : `option${o}`"
                :value="idKey ? option[idKey] : option"
                :disabled="disabledValues.includes(idKey ? option[idKey] : option)"
                v-slot="{ active, selected }">
                <li
                  :class="[
                    active ? 'bg-accent-600 text-white' : 'text-neutral-900 dark:text-neutral-300',
                    'relative cursor-pointer select-none py-2 pl-8 pr-4 text-left'
                  ]">
                  <span
                    :class="[
                      selected ? 'font-semibold' : 'font-normal',
                      'block',
                      disabledValues.includes(idKey ? option[idKey] : option) ? 'opacity-30' : null
                    ]">
                    {{ option[nameKey] }}
                  </span>

                  <span
                    v-if="selected"
                    :class="[
                      active ? 'text-white' : 'text-accent-600',
                      'absolute inset-y-0 left-0 flex items-center pl-1.5'
                    ]">
                    <i class="fa-sharp fa-regular fa-check" aria-hidden="true"></i>
                  </span>
                </li>
              </ListboxOption>
            </div>
          </template>
          <template v-else>
            <ListboxOption
              as="template"
              v-for="(option, o) in resolvedOptions"
              :key="idKey ? option[idKey] : `option${o}`"
              :value="idKey ? option[idKey] : option"
              :disabled="disabledValues.includes(idKey ? option[idKey] : option)"
              v-slot="{ active, selected }">
              <li
                :class="[
                  active ? 'bg-accent-600 text-white' : 'text-neutral-900 dark:text-neutral-300',
                  'relative cursor-pointer select-none py-2 pl-8 pr-4 text-left'
                ]">
                <span
                  :class="[
                    selected ? 'font-semibold' : 'font-normal',
                    'block',
                    disabledValues.includes(idKey ? option[idKey] : option) ? 'opacity-30' : null
                  ]">
                  {{ option[nameKey] }}
                </span>

                <span
                  v-if="selected"
                  :class="[
                    active ? 'text-white' : 'text-accent-600',
                    'absolute inset-y-0 left-0 flex items-center pl-1.5'
                  ]">
                  <i class="fa-sharp fa-regular fa-check" aria-hidden="true"></i>
                </span>
              </li>
            </ListboxOption>
          </template>
        </ListboxOptions>
      </Float>

      <NtButton
        v-if="suffixIcon"
        level="primary"
        :icon="suffixIcon"
        class="rounded-l-none"
        :disabled="disabled || formDisabled"
        @click="$emit('suffix-click')" />
    </div>

    <TransitionGroup
      v-if="tags && tagButtons.length > 0"
      tag="div"
      class="flex flex-wrap gap-3 mt-3 empty:mt-0"
      move-class="transition-all duration-500"
      enter-active-class="transition-all duration-500"
      leave-active-class="absolute transition-all duration-200"
      enter-from-class="scale-90 opacity-0"
      leave-to-class="scale-75 opacity-0">
      <NtButton
        v-for="tag in tagButtons"
        :key="tag[idKey]"
        icon="fa-times-circle"
        :disabled="disabled || formDisabled || disabledValues.includes(tag[idKey])"
        @click="Array.isArray(content) ? $helpers.removeItem(tag[idKey], content) : (content = null)">
        {{ tag[nameKey] }}
      </NtButton>
    </TransitionGroup>

    <p v-if="error" class="mt-2 text-sm text-red-600" :id="hintId">
      <i v-if="error" class="text-red-600 fa-sharp fa-regular fa-exclamation-circle" aria-hidden="true"></i>
      {{ error }}
    </p>
    <p v-else-if="hint" class="mt-2 text-sm text-neutral-500 dark:text-neutral-400" :id="hintId">
      {{ hint }}
    </p>
  </Listbox>
</template>

<script>
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue'
import Str from 'laravel-js-str'
import { isEqual } from 'lodash'
import NtButton from '../general/NtButton.vue'
import { Float } from '@headlessui-float/vue'

export default {
  components: {
    Listbox,
    ListboxButton,
    ListboxLabel,
    ListboxOption,
    ListboxOptions,
    NtButton,
    Float
  },
  emits: ['update:model-value', 'change', 'change-full', 'suffix-click'],
  inject: {
    form: {
      default: null
    },
    formDisabled: {
      default: false
    }
  },
  props: {
    label: {
      type: String,
      default: null
    },
    name: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    hint: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disabledValues: {
      type: Array,
      default: () => []
    },
    modelValue: {
      default: null
    },
    options: {
      type: [Array, Object],
      required: true
    },
    idKey: {
      type: String,
      default: 'id'
    },
    nameKey: {
      type: String,
      default: 'name'
    },
    mobileUp: {
      type: Boolean,
      default: false
    },
    up: {
      type: Boolean,
      default: false
    },
    unknown: {
      type: [String, Number],
      default: 'Unknown Selection'
    },
    tags: {
      type: Boolean,
      default: false
    },
    selectClass: {
      type: [null, String],
      default: null
    },
    highlight: {
      type: Boolean,
      default: false
    },
    suffixIcon: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      resolvedOptions: [],
      loading: false
    }
  },
  computed: {
    id() {
      return Str.random()
    },
    hintId() {
      return this.hint || this.error ? `${this.id}Hint` : null
    },
    classes() {
      if (this.error) {
        return 'bg-red-100 dark:bg-neutral-700 text-red-900 dark:text-neutral-100 ring-red-300 dark:ring-red-500 placeholder:text-red-300 focus:ring-red-500'
      }

      if (this.highlight && !this.disabled && !this.formDisabled) {
        return 'bg-yellow-100 dark:bg-yellow-950 text-yellow-900 dark:text-neutral-100 ring-yellow-300 dark:ring-yellow-500 placeholder:text-yellow-300 focus:ring-yellow-500'
      }

      return 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 ring-neutral-300 dark:ring-neutral-600 focus:ring-accent-600 dark:focus:ring-accent-500 disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-500 disabled:ring-neutral-200'
    },
    error() {
      if (!this.form?.errors || !this.name) {
        return null
      }
      return this.form.errors[this.name]
    },
    content: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:model-value', value)
        this.change()
        this.$emit('change', value)

        this.$nextTick(() => this.emitAll())
      }
    },
    selectedName() {
      if (Array.isArray(this.content)) {
        if (this.content.length === 0) {
          return this.placeholder
        }
        if (this.content.length !== 1) {
          return `${this.content.length} Selected`
        }

        const item =
          typeof this.resolvedOptions === 'object' && !Array.isArray(this.resolvedOptions)
            ? Object.values(this.resolvedOptions)
                .flat(1)
                .find((o) => (this.idKey ? o[this.idKey] : o) === this.content[0])
            : this.resolvedOptions.find((o) => (this.idKey ? o[this.idKey] : o) === this.content[0])
        return item ? item[this.nameKey] : this.loading ? 'Loading...' : this.unknown
      }

      if (this.content === null) {
        return this.placeholder
      }

      const item =
        typeof this.resolvedOptions === 'object' && !Array.isArray(this.resolvedOptions)
          ? Object.values(this.resolvedOptions)
              .flat(1)
              .find((o) => (this.idKey ? o[this.idKey] : o) === this.content)
          : this.resolvedOptions.find((o) => (this.idKey ? o[this.idKey] : o) === this.content)
      return item ? item[this.nameKey] : this.loading ? 'Loading...' : this.unknown
    },
    tagButtons() {
      return this.resolvedOptions.filter((o) =>
        Array.isArray(this.content) ? this.content.includes(o[this.idKey]) : this.content === o[this.idKey]
      )
    }
  },
  methods: {
    change() {
      if (!this.form || !this.name) {
        return
      }
      this.form.validate(this.name)
    },
    emitAll() {
      const options = Array.isArray(this.resolvedOptions)
        ? this.resolvedOptions
        : Object.values(this.resolvedOptions).flat(1)

      this.$emit('change-full', options.find((o) => o[this.idKey] === this.content) || null)
    }
  },
  watch: {
    options: {
      async handler(newVal, oldVal) {
        if (isEqual(newVal, oldVal)) {
          return
        }
        this.resolvedOptions = []
        if (typeof newVal === 'object' && !Array.isArray(newVal) && !newVal.endpoint) {
          this.resolvedOptions = newVal
          this.$nextTick(() => this.emitAll())
          return
        }

        if (!Array.isArray(this.content) && this.placeholder) {
          let item = {}
          item[this.idKey] = null
          item[this.nameKey] = this.placeholder
          this.resolvedOptions.push(item)
        }
        if (Array.isArray(this.options)) {
          this.resolvedOptions = this.resolvedOptions.concat(this.options)
          return
        }

        this.loading = true
        const results = await this.$api.get(this.options.endpoint, {
          params: {
            ...this.options.filters,
            list: true
          }
        })
        this.resolvedOptions = this.resolvedOptions.concat(results.data)

        if (Array.isArray(this.content)) {
          const filtered = this.content.filter((o) => this.resolvedOptions.find((ro) => ro[this.idKey] === o))

          if (this.content.length !== filtered.length) {
            this.content = filtered
          }
        } else if (!this.resolvedOptions.find((o) => (this.idKey ? o[this.idKey] : o) === this.content)) {
          console.info(`Select option "${this.content}" not found on list "${this.name}". Changing to null.`)
          this.content = null
        }

        this.$nextTick(() => this.emitAll())
        this.loading = false
      },
      immediate: true
    }
  }
}
</script>
